import { MNSproCloudApi } from '..';
import { LicenseInfo, TenantCredentialsResponse, TenantResponse } from '../api/v2';

export type { LicenseInfo } from '../api/v2';

export class TenantService {
  private static createTenantsApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.TenantsApiFactory(config);

    return api;
  }

  /**
   * Gets all tenants, the current user has access to.
   *
   * @export
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<TenantResponse[]>}
   */
  static async getAll(authToken: string): Promise<TenantResponse[]> {
    const api = TenantService.createTenantsApi(authToken);

    const r = await api.getAll();
    return r.data;
  }

  /**
   * Gets a specific tenant by its id.
   *
   * @export
   * @param {string} id E.g. b1948480-ab44-4d31-ba9d-7c2c756dc2bc
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @return {*}  {Promise<TenantResponse>}
   */
  static async getById(id: string, authToken: string): Promise<TenantResponse> {
    const api = TenantService.createTenantsApi(authToken);

    const r = await api.get(id);
    return r.data;
  }

  /**
   * Gets all Microsoft licenses assignable to individual users for the given
   * tenant, including their purchased (paid) and consumed unit counts.
   *
   * @static
   * @param {string} authToken JWT (without 'Bearer'-prefix)
   * @param {string} tenantId E.g. b1948480-ab44-4d31-ba9d-7c2c756dc2bc
   * @return {Promise<LicenseInfo[]>}
   * @memberof TenantService
   */
  static async getLicenses(authToken: string, tenantId: string): Promise<LicenseInfo[]> {
    const api = TenantService.createTenantsApi(authToken);
    const r = await api.getLicenses(tenantId);
    return r.data;
  }

  /**
   *
   * @param authToken
   * @param tenantId
   * @param reason
   * @returns {Promise<TenantCredentialsResponse>}
   */
  static async getCredentials(
    authToken: string,
    tenantId: string,
    reason: string,
  ): Promise<TenantCredentialsResponse> {
    const api = TenantService.createTenantsApi(authToken);
    const response = await api.getCredentials(tenantId, reason);

    return response.data;
  }

  /**
   * Will return true if the tenant of the current requesting user is a known
   * customer.
   *
   * Otherwise it will return false.
   *
   * @static
   * @param {string} authToken
   * @return {*}
   * @memberof TenantService
   */
  static async getIsValid(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.TenantsApiFactory(config);

    try {
      await api.hasPaid();
      return true;
    } catch {
      return false;
    }
  }

  static async deleteTenant(
    authToken: string,
    tenantId: string,
    deleteInvitation: boolean,
  ) {
    const api = TenantService.createTenantsApi(authToken);
    await api._delete(tenantId, deleteInvitation);
  }
}
